/* IS THIS CHAMPIONSHIP STILL WINNABLE BUSINESS?
 *
 * Brian: "i want to make sure we are counting the championships from when i
 * launch this beast to the team. i do not want the old championships being
 * included in the count like the 2025 or 2026 nba championship. they are
 * already spoken for."
 *
 * ⚠️ THE COUNTS HAVE BEEN MEASURING THE OBJECT, NOT THE OPPORTUNITY. Every
 * total on the board — 14,200 records, 136 FHSAA championships, "15,000 to
 * pursue" — counts every row we hold, including titles decided years ago whose
 * rings were bought from somebody else. That number cannot be worked. A rep
 * handed it is being told to chase a season that is over.
 *
 * The 2025 NBA championship is not a lead. It is history.
 *
 * ⚠️ AND "SPOKEN FOR" IS TWO DIFFERENT THINGS, which is why one date check is
 * not enough:
 *
 *   BEHIND THE LAUNCH  — the event happened before we turned the machine on.
 *                        Whoever won it bought their rings without us.
 *   ALREADY CROWNED    — a champion is recorded. Even if the date is ahead of
 *                        the launch, that title is decided and the order is
 *                        placed or lost. Counting it as pipeline is a lie the
 *                        forecast pays for.
 *
 * A record has to clear BOTH to be real pipeline.
 *
 * ⚠️ AND NOTHING HERE DELETES OR HIDES A RECORD. History is why the champion
 * hunt, the incumbent field and the rep's talking points work — "you beat Oak
 * Hill 28-21 last February" comes straight out of it. This module only decides
 * what gets COUNTED as opportunity. Archiving the past would break the pitch
 * that sells the future.
 *
 * Pure. No React, no fetch.
 */

const S = (v) => String(v == null ? '' : v).trim()

/* ⚠️ THE LAUNCH DATE IS THE LINE, and it is declared in ONE place so a number
 * on a card and a number in a forecast can never disagree about which season
 * they are describing. Change it here and every count moves together.
 *
 * Set to the team rollout. Before this, SR was not working the board. */
export const LAUNCH_DATE = '2026-09-01'

const asTime = (v) => {
  const s = S(v)
  if (!s) return null
  /* HubSpot hands back epoch-ms as often as ISO, and a bare number parsed as a
   * date string silently becomes NaN. */
  if (/^\d{10,}$/.test(s)) return Number(s)
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : null
}

export const PURSUABLE = 'pursuable'
export const CROWNED = 'crowned'
export const BEFORE_LAUNCH = 'before-launch'
export const UNDATED = 'undated'

/**
 * Can a rep still win this?
 *
 * @returns { pursuable, status, reason }
 */
export function pursuability(record = {}, { launch = LAUNCH_DATE, now = Date.now() } = {}) {
  const raw = record?.raw || record || {}
  const launchTime = asTime(launch) ?? 0

  /* ⚠️ CROWNED IS CHECKED FIRST, and beats the date. A championship with a
   * recorded champion is decided whatever its date says — a future date on a
   * crowned record means the date is wrong, not that the title is open. */
  const champion = S(raw.current_champion ?? record.champion)
  if (champion) {
    return { pursuable: false, status: CROWNED, reason: `Already won by ${champion} — the ring order is placed or lost.` }
  }

  const t = asTime(raw.sr_event_date ?? record.effDate ?? record.eventDate)

  /* ⚠️ AN UNDATED RECORD IS NOT AUTOMATICALLY EXCLUDED. Roughly one in ten
   * carries no event date, and a good share of those are standings-decided
   * titles that are entirely live business. Dropping them from the count to
   * make the arithmetic simple would delete real pipeline — so they count, and
   * are labelled so the number can be interrogated. */
  if (t == null) {
    return { pursuable: true, status: UNDATED, reason: 'No event date recorded, so it cannot be ruled out — counted as open until dated.' }
  }

  if (t < launchTime) {
    return {
      pursuable: false,
      status: BEFORE_LAUNCH,
      reason: `Decided before the ${S(launch)} launch — whoever won it bought their rings without us.`,
    }
  }

  return { pursuable: true, status: PURSUABLE, reason: 'Ahead of the launch and no champion recorded — live business.' }
}

/** Convenience: the boolean on its own. */
export const isPursuable = (record, opts) => pursuability(record, opts).pursuable

/**
 * Split a set of records into what can be worked and what cannot.
 *
 * ⚠️ RETURNS BOTH HALVES ON PURPOSE. A caller that only got the pursuable list
 * would have no way to show "1,412 of 14,183 are live" — and a total with no
 * denominator is exactly the kind of number nobody can sanity-check.
 */
export function splitPursuable(records = [], opts = {}) {
  const live = []
  const crowned = []
  const past = []
  const undated = []
  for (const r of records) {
    const p = pursuability(r, opts)
    if (p.status === CROWNED) crowned.push(r)
    else if (p.status === BEFORE_LAUNCH) past.push(r)
    else if (p.status === UNDATED) { live.push(r); undated.push(r) }
    else live.push(r)
  }
  return {
    live, crowned, past, undated,
    total: records.length,
    pursuable: live.length,
    spokenFor: crowned.length + past.length,
  }
}
